import React, { useEffect, useState } from "react";
import { FaStar, FaCrown, FaTrophy, FaRocket } from "react-icons/fa";
import { FiCheckCircle, FiTrendingUp, FiShoppingBag, FiCreditCard } from "react-icons/fi";
import { jwtDecode } from "jwt-decode";
import * as api from "../../api/index";
import { IoMdArrowBack } from "react-icons/io";
import { useNavigate } from "react-router-dom";

const levels = [
  {
    id: 1,
    title: "Beginner",
    subtitle: "Level 1",
    description: "Start your journey! Unlock basic features and get familiar with the platform.",
    tasks: ["Create an account", "Complete 5 orders", "Spending Over ₦100,000"],
    perks: ["Access to all basic products"],
    icon: <FaRocket />,
    color: "#3b82f6",
  },
  {
    id: 2,
    title: "Regular",
    subtitle: "Level 2",
    description: "You’re getting the hang of it! Enjoy better perks and faster support.",
    tasks: ["Complete 10 orders", "Spending Over ₦250,000"],
    perks: ["5% discount on select products", "Monthly giveaways"],
    icon: <FaStar />,
    color: "#10b981",
  },
  {
    id: 3,
    title: "Pro Buyer",
    subtitle: "Level 3",
    description: "Almost there! Get exclusive offers and early access to new products.",
    tasks: ["Complete 20 orders", "Spending Over ₦1,000,000"],
    perks: ["10% store-wide discount", "VIP Buyer support"],
    icon: <FaTrophy />,
    color: "#f59e0b",
  },
  {
    id: 4,
    title: "VIP",
    subtitle: "Level 4",
    description: "You’re at the top! Enjoy maximum rewards, premium perks, and personal support.",
    tasks: ["Complete 50 orders", "Spending Over ₦2,500,000"],
    perks: [
      "20% store-wide discount",
      "Dedicated manager",
      "International Delivery discount",
      "Exclusive VIP products",
    ],
    icon: <FaCrown />,
    color: "#8b5cf6",
  },
];

export default function Levels() {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLevel = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) throw new Error("No token found. Please log in again.");

        const decoded = jwtDecode(token);
        if (!decoded?.id) throw new Error("Invalid token format.");

        const userId = decoded.id;
        const res = await api.updateLevel(userId);

        if (!res?.data?.data) throw new Error("Invalid response from server.");

        setUserData(res.data.data); 
      } catch (err) {
        setError("Could not fetch buyer level data.");
      } finally {
        setLoading(false);
      }
    };

    fetchLevel();
  }, []);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
        <div className="w-12 h-12 border-4 border-orange-200 border-t-orange-600 rounded-full animate-spin"></div>
        <p className="mt-4 font-black text-gray-400 uppercase tracking-widest text-[10px]">Analyzing Activity...</p>
      </div>
    );
  }

  const currentLevel = userData?.level || 1;

  return (
    <div className="bg-gray-50 min-h-screen pb-20">
      {/* 🔝 Sticky Header */}
      <div className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b px-4 py-4 flex items-center justify-between">
        <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-full transition text-gray-700">
          <IoMdArrowBack size={24} />
        </button>
        <h1 className="text-lg font-black text-gray-900 tracking-tight text-center flex-1 pr-8">Level Journey</h1>
      </div>

      <div className="max-w-2xl mx-auto p-4 sm:p-6">
        {/* 💳 Buyer Performance Card */}
        <div className="bg-white p-6 rounded-[2.5rem] shadow-xl shadow-gray-200/50 border border-gray-100 mb-10">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-orange-600 rounded-3xl flex items-center justify-center text-white shadow-lg shadow-orange-100 shrink-0">
              <FiTrendingUp size={28} strokeWidth={2.5} />
            </div>
            <div>
              <p className="text-[10px] font-black text-orange-600 uppercase tracking-[0.2em] mb-1">Shopping Tier</p>
              <h2 className="text-2xl font-black text-gray-900">Rank: {levels.find(l => l.id === currentLevel)?.title}</h2>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 p-4 rounded-2xl flex items-center gap-3">
              <div className="text-gray-400 bg-white p-2 rounded-lg"><FiShoppingBag /></div>
              <div>
                <p className="text-[10px] font-black text-gray-400 uppercase">Orders</p>
                <p className="text-sm font-black text-gray-900">{userData.totalOrders}</p>
              </div>
            </div>
            <div className="bg-gray-50 p-4 rounded-2xl flex items-center gap-3">
              <div className="text-gray-400 bg-white p-2 rounded-lg"><FiCreditCard /></div>
              <div>
                <p className="text-[10px] font-black text-gray-400 uppercase">Spend</p>
                <p className="text-sm font-black text-gray-900">₦{userData.totalSpent.toLocaleString()}</p>
              </div>
            </div>
          </div>
        </div>

        {error && <div className="text-center text-red-600 font-bold bg-red-50 p-4 rounded-2xl mb-6 border border-red-100">{error}</div>}

        {/* 🗺 Journey Progress Timeline */}
        <div className="space-y-8 relative">
          <div className="absolute left-8 top-10 bottom-10 w-0.5 bg-gray-200 -z-0" />

          {levels.map((level) => {
            const isCurrent = level.id === currentLevel;
            const isUnlocked = level.id <= currentLevel;

            return (
              <div
                key={level.id}
                className={`relative z-10 p-6 rounded-[2rem] border transition-all duration-500 ${
                  isCurrent 
                  ? "bg-white shadow-2xl shadow-gray-200 border-orange-200 scale-[1.02]" 
                  : isUnlocked ? "bg-white border-gray-100" : "bg-white/40 border-gray-100 opacity-60"
                }`}
              >
                {/* Visual Connector Dot */}
                <div className={`absolute -left-10 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full border-4 border-gray-50 z-20 ${isCurrent ? 'bg-orange-600 animate-pulse' : isUnlocked ? 'bg-green-500' : 'bg-gray-300'}`} />

                <div className="flex justify-between items-start mb-5">
                  <div className="flex items-center gap-4">
                    <div className={`p-4 rounded-2xl text-2xl transition-all ${isCurrent ? 'bg-orange-600 text-white shadow-lg shadow-orange-100' : 'bg-gray-100 text-gray-400'}`}>
                      {level.icon}
                    </div>
                    <div>
                      <p className={`text-[10px] font-black uppercase tracking-widest ${isCurrent ? 'text-orange-600' : 'text-gray-400'}`}>
                        {level.subtitle}
                      </p>
                      <h3 className="text-xl font-black text-gray-900">{level.title}</h3>
                    </div>
                  </div>
                  {isCurrent && (
                    <span className="bg-orange-100 text-orange-700 text-[10px] font-black px-3 py-1 rounded-full uppercase">Current Status</span>
                  )}
                </div>

                <p className="text-sm text-gray-500 font-medium mb-6 leading-relaxed">
                  {level.description}
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {/* Goal Section */}
                  <div>
                    <h4 className="text-[10px] font-black text-gray-900 uppercase tracking-widest mb-3 flex items-center gap-2">
                       The Mission
                    </h4>
                    <ul className="space-y-2">
                      {level.tasks.map((task, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-xs font-bold text-gray-600">
                          <FiCheckCircle className={isUnlocked ? "text-green-500" : "text-gray-300"} /> {task}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Rewards Section */}
                  <div>
                    <h4 className="text-[10px] font-black text-gray-900 uppercase tracking-widest mb-3">
                      Rank Rewards
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {level.perks.map((perk, idx) => (
                        <span key={idx} className="bg-gray-50 text-gray-400 text-[9px] font-black px-2 py-1 rounded-lg border border-gray-100 uppercase">
                          {perk}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Progress Visualizer for Current Rank */}
                {isCurrent && (
                  <div className="mt-8 pt-6 border-t border-gray-50">
                    <div className="flex justify-between items-end mb-2">
                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Next Milestone Progress</p>
                        <p className="text-xs font-black text-orange-600">{userData.progress}%</p>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
                      <div
                        className="h-full bg-orange-600 rounded-full transition-all duration-[1.5s] ease-out"
                        style={{ width: `${userData.progress}%` }}
                      ></div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}